import { memo, useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { CardBody } from '@m/alchemy-ui/CardBody';
import { ActionList } from '@m/alchemy-ui/ActionList';
import { Dialog } from '@m/alchemy-ui/Dialog';
import { FieldStatus } from '@m/alchemy-ui/FieldStatus';
import { MoreMenuButton } from '@m/alchemy-ui/MoreMenuButton';
import { PopperBox } from '@m/alchemy-ui/PopperBox';
import { Select } from '@m/alchemy-ui/Select';
import { DeleteIcon } from '@m/alchemy-ui/icons/DeleteIcon';
import { DragDropIcon } from '@m/alchemy-ui/icons/DragDropIcon';
import { type PanelConfigPatch } from '../common/panel-grid';
import {
  AUTO_PANEL_SIZE,
  type PageCmsPanel,
  type PanelSize,
  panelTypesByTypename,
  WIDE_PANEL_SIZE
} from '../common/panel-types';
import { getPanelCardError, getPanelConfigError } from '../common/panel-validation';
import {
  PanelCard,
  PanelCardHeader,
  PanelErrorStatus,
  PanelHeaderTitle,
  PanelInfoText,
  PanelLine,
  PanelSizeMenuLabel,
  PanelSizeMenuSection
} from '../CustomPage.styled';
import { ChallengeCarouselPanelEditor } from './panel-editors/ChallengeCarouselPanelEditor';
import { IdeasPanelEditor } from './panel-editors/IdeasPanelEditor';
import { ImagePanelEditor } from './panel-editors/ImagePanelEditor';
import { LeaderboardPanelEditor } from './panel-editors/LeaderboardPanelEditor';
import { TextPanelEditor } from './panel-editors/TextPanelEditor';
import { VideoPanelEditor } from './panel-editors/VideoPanelEditor';

const messages = defineMessages({
  panelSize: {
    id: 'editCustomPagePanelSize',
    defaultMessage: 'Panel size'
  },
  panelSizeAuto: {
    id: 'editCustomPagePanelSizeAuto',
    defaultMessage: 'Auto'
  },
  panelSizeWide: {
    id: 'editCustomPagePanelSizeWide',
    defaultMessage: 'Wide'
  },
  panelOptions: {
    id: 'editCustomPagePanelOptions',
    defaultMessage: 'Options for {panel}'
  },
  deletePanel: {
    id: 'editCustomPageDeletePanel',
    defaultMessage: 'Delete panel'
  },
  deletePanelConfirmation: {
    id: 'editCustomPageDeletePanelConfirmation',
    defaultMessage: 'Are you sure you want to delete this element?'
  },
  activityInfo: {
    id: 'editCustomPagePanelActivityInfo',
    defaultMessage: 'Displays recent user and challenge activity'
  },
  blogInfo: {
    id: 'editCustomPagePanelBlogInfo',
    defaultMessage: 'Displays latest blog posts'
  }
});

interface PanelItemProps {
  readonly panel: PageCmsPanel;
  readonly panelCount: number;
  readonly rowIndex: number;
  readonly onSizeChange: (rowIndex: number, panelId: string, size: PanelSize) => void;
  readonly onDeletePanel: (rowIndex: number, panelId: string) => void;
  readonly onConfigChange: (rowIndex: number, panelId: string, patch: PanelConfigPatch) => void;
  readonly showPanelErrors: boolean;
}

const PanelBodyComponent = ({
  config,
  panelId,
  showPanelErrors,
  onConfigChange
}: {
  readonly config: PageCmsPanel['settings'];
  readonly panelId: string;
  readonly showPanelErrors: boolean;
  readonly onConfigChange: (patch: PanelConfigPatch) => void;
}) => {
  const intl = useIntl();

  switch (config.__typename) {
    case 'LinePanelSettings':
      return <PanelLine />;
    case 'ActivityPanelSettings':
      return <PanelInfoText>{intl.formatMessage(messages.activityInfo)}</PanelInfoText>;
    case 'BlogCarouselPanelSettings':
      return <PanelInfoText>{intl.formatMessage(messages.blogInfo)}</PanelInfoText>;
    case 'LeaderboardPanelSettings':
      return <LeaderboardPanelEditor config={config} onConfigChange={onConfigChange} />;
    case 'IdeasPanelSettings':
      return <IdeasPanelEditor config={config} onConfigChange={onConfigChange} />;
    case 'ChallengeCarouselPanelSettings':
      return <ChallengeCarouselPanelEditor config={config} onConfigChange={onConfigChange} />;
    case 'VideoPanelSettings':
      return <VideoPanelEditor config={config} onConfigChange={onConfigChange} />;
    case 'TextPanelSettings':
      return (
        <TextPanelEditor
          config={config}
          panelId={panelId}
          showPanelErrors={showPanelErrors}
          onConfigChange={onConfigChange}
        />
      );
    case 'ImagePanelSettings':
      return (
        <ImagePanelEditor
          config={config}
          panelId={panelId}
          showPanelErrors={showPanelErrors}
          onConfigChange={onConfigChange}
        />
      );
    default:
      return null;
  }
};

// Memoized on its settings/handlers; re-renders when the panel's settings object changes.
const PanelBody = memo(PanelBodyComponent);

const PanelItemComponent = ({
  panel,
  panelCount,
  rowIndex,
  onSizeChange,
  onDeletePanel,
  onConfigChange,
  showPanelErrors
}: PanelItemProps) => {
  const intl = useIntl();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const panelType = panelTypesByTypename[panel.settings.__typename];
  const panelName = intl.formatMessage(panelType.message);

  // "wide" only applies in 2-panel rows — same rule as the public renderer (PageItem.tsx).
  const isWide = panelCount === 2 && panel.settings.size === WIDE_PANEL_SIZE;
  const sizeLabelId = `panel-size-label-${panel.id}`;
  const panelId = panel.id;

  // Derived from the config (not stored) so errors clear as soon as the panel is fixed; only
  // surfaced after a save attempt, matching the page-title validation. Video and link-config
  // error messages are rendered inside their editors, so the card shows only the border for them.
  const hasError = showPanelErrors && getPanelConfigError(panel.settings) !== null;
  const cardError = showPanelErrors ? getPanelCardError(panel.settings) : null;

  const handleConfigChange = useCallback(
    (patch: PanelConfigPatch) => onConfigChange(rowIndex, panelId, patch),
    [onConfigChange, rowIndex, panelId]
  );

  const sizeOptions = useMemo(
    () => [
      { value: AUTO_PANEL_SIZE as string, label: intl.formatMessage(messages.panelSizeAuto) },
      { value: WIDE_PANEL_SIZE as string, label: intl.formatMessage(messages.panelSizeWide) }
    ],
    [intl]
  );

  const closeConfirm = useCallback(() => setConfirmOpen(false), []);

  const handleConfirmDelete = useCallback(() => {
    setConfirmOpen(false);
    onDeletePanel(rowIndex, panelId);
  }, [onDeletePanel, rowIndex, panelId]);

  const footerButtons = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' }),
        onClick: closeConfirm,
        priority: 'tertiary' as const
      },
      {
        label: intl.formatMessage({ id: 'delete', defaultMessage: 'Delete' }),
        onClick: handleConfirmDelete,
        destructive: true
      }
    ],
    [intl, closeConfirm, handleConfirmDelete]
  );

  return (
    <PanelCard
      $isWide={isWide}
      $hasError={hasError}
      hasBorder
      shadowDepth="none"
      data-testid={`panel-item-${panel.id}`}
      data-size={isWide ? WIDE_PANEL_SIZE : AUTO_PANEL_SIZE}
    >
      <PanelCardHeader>
        <PanelHeaderTitle>
          <DragDropIcon size={20} aria-hidden="true" />
          {panelName}
        </PanelHeaderTitle>
        <MoreMenuButton
          aria-label={intl.formatMessage(messages.panelOptions, { panel: panelName })}
          placement="bottom-end"
          // Custom overlay instead of `actions`: the size Select can't live in the default
          // ActionList overlay, and MenuListItemRadio is not an exported @m/alchemy-ui subpath.
          overlay={({ closeAndReturnFocus }) => (
            <PopperBox hasPadding={false} maxWidth={260}>
              <div>
                {panelCount === 2 && (
                  <PanelSizeMenuSection>
                    <PanelSizeMenuLabel id={sizeLabelId}>{intl.formatMessage(messages.panelSize)}</PanelSizeMenuLabel>
                    <Select
                      labelId={sizeLabelId}
                      isFullWidth
                      value={isWide ? WIDE_PANEL_SIZE : AUTO_PANEL_SIZE}
                      options={sizeOptions}
                      onChange={(option) => {
                        onSizeChange(rowIndex, panelId, option.value as PanelSize);
                        closeAndReturnFocus();
                      }}
                    />
                  </PanelSizeMenuSection>
                )}
                <ActionList
                  actions={[
                    {
                      label: intl.formatMessage(messages.deletePanel),
                      icon: <DeleteIcon aria-hidden="true" />,
                      onClick: () => {
                        closeAndReturnFocus();
                        setConfirmOpen(true);
                      }
                    }
                  ]}
                />
              </div>
            </PopperBox>
          )}
        />
      </PanelCardHeader>
      <CardBody>
        {cardError && (
          <PanelErrorStatus>
            <FieldStatus level="error" message={intl.formatMessage(cardError)} />
          </PanelErrorStatus>
        )}
        <PanelBody
          config={panel.settings}
          panelId={panelId}
          showPanelErrors={showPanelErrors}
          onConfigChange={handleConfigChange}
        />
      </CardBody>
      <Dialog
        isOpen={confirmOpen}
        onClose={closeConfirm}
        headerContent={intl.formatMessage({ id: 'areYouSure', defaultMessage: 'Are you sure?' })}
        bodyContent={intl.formatMessage(messages.deletePanelConfirmation)}
        footerButtons={footerButtons}
      />
    </PanelCard>
  );
};

// Immutable panel updates make prop comparison cheap and correct: a panel re-renders only when
// its own data, its row's panel count, or the grid callbacks change.
export const PanelItem = memo(PanelItemComponent);
